import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { ApiButtonLayout, ApiRegister } from '@vend/types'
import {
  Button,
  Flag,
  Icon,
  Text,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import {
  useQuickKeysManager,
  useRegisterManager,
  useSyncManager,
} from '../../ng-compat/hooks'
import { $state } from '../../ng-compat/imports'
import { STATES } from '../../../../lib/routes'
import { registersStore } from '../../factories/database/registers-store'
import { DeleteQuickKeyLayoutModal } from './DeleteQuickKeyLayoutModal'

export const QuickKeysLayouts: FunctionComponent = () => {
  const t = useT()
  const quickKeysManager = useQuickKeysManager()
  const registerManager = useRegisterManager()
  const syncManager = useSyncManager()
  const [layouts, setLayouts] = useState<ApiButtonLayout[]>([])
  const [processing, setProcessing] = useState<string[]>([])
  const [toDelete, setToDelete] = useState<ApiButtonLayout | null>(null)
  const [inUseLayouts, setInUseLayouts] = useState<
    Record<string, ApiRegister[]>
  >({})

  const setLayoutProcessing = useCallback((layoutId: string) => {
    setProcessing(prev => [...prev, layoutId])
  }, [])

  const clearLayoutProcessing = useCallback((layoutId: string) => {
    setProcessing(prev => prev.filter(id => id !== layoutId))
  }, [])

  const isCurrentLayout = useCallback(
    (layout: ApiButtonLayout) =>
      quickKeysManager.quickKeyLayout?.id === layout.id,
    [quickKeysManager.quickKeyLayout?.id]
  )

  const getLayouts = useCallback(async () => {
    await quickKeysManager.init()
    const allLayouts = await quickKeysManager.getAllQuickKeyLayouts()
    const currentLayout = (await quickKeysManager.quickKeyLayout) as ApiButtonLayout
    if (!currentLayout) {
      setLayouts(allLayouts)
      return
    }
    setLayouts([currentLayout, ...allLayouts.filter(l => !isCurrentLayout(l))])
  }, [isCurrentLayout, quickKeysManager])

  useEffect(() => {
    getLayouts()

    quickKeysManager.on('quickKeyLayoutChange', getLayouts)
    syncManager.on('syncComplete', getLayouts)

    return () => {
      quickKeysManager.off('quickKeyLayoutChange', getLayouts)
      syncManager.off('syncComplete', getLayouts)
    }
  }, [getLayouts, isCurrentLayout, quickKeysManager, syncManager])

  useEffect(() => {
    const setLayoutUsage = async () => {
      const activeRegister = registerManager.getActiveRegister()
      const allRegisters = await registersStore.all()
      const layoutsInUse: Record<string, ApiRegister[]> = {}
      allRegisters.forEach(register => {
        const quickKeyLayoutId = register.button_layout_id
        if (
          (activeRegister && activeRegister.id === register.id) ||
          !quickKeyLayoutId
        ) {
          return
        }
        if (!layoutsInUse[quickKeyLayoutId]) {
          layoutsInUse[quickKeyLayoutId] = [register]
        } else {
          layoutsInUse[quickKeyLayoutId].push(register)
        }
      })

      setInUseLayouts(layoutsInUse)
    }
    setLayoutUsage()
  }, [registerManager])

  const setCurrentLayout = useCallback(
    async (layout: ApiButtonLayout) => {
      setLayoutProcessing(layout.id)
      try {
        await registerManager.setCurrentQuickKeyLayout(layout)
        clearLayoutProcessing(layout.id)
      } catch {
        clearLayoutProcessing(layout.id)
        ToastNotifications.negative(
          t(
            'Sorry there was a problem setting this layout as the current layout, please try again'
          )
        )
      }
    },
    [clearLayoutProcessing, registerManager, setLayoutProcessing, t]
  )

  const duplicateLayout = useCallback(
    async (layout: ApiButtonLayout) => {
      setLayoutProcessing(layout.id)
      try {
        await quickKeysManager.duplicateLayout(layout)
        ToastNotifications.success(
          t('Layout queued for duplication and will appear shortly')
        )
        clearLayoutProcessing(layout.id)
      } catch {
        clearLayoutProcessing(layout.id)
        ToastNotifications.negative(
          t(
            'Sorry there was a problem duplicating this layout, please try again'
          )
        )
      }
    },
    [clearLayoutProcessing, quickKeysManager, setLayoutProcessing, t]
  )

  const openDeleteModal = useCallback(
    (layout: ApiButtonLayout) => {
      if (inUseLayouts[layout.id]?.length) {
        const registerNames = inUseLayouts[layout.id].map(r => r.name)
        ToastNotifications.negative(
          t(
            "Unable to delete this layout as it's currently being used by {names}",
            { names: registerNames.join(', ') }
          )
        )
        return
      }
      setToDelete(layout)
    },
    [inUseLayouts, t]
  )

  const closeDeleteModal = useCallback(() => {
    setToDelete(null)
  }, [])

  const deleteLayout = useCallback(async () => {
    if (!toDelete) {
      return
    }
    const layout = toDelete
    setToDelete(null)
    setLayoutProcessing(toDelete.id)
    try {
      await quickKeysManager.deleteLayout(layout)
      await getLayouts()
      clearLayoutProcessing(layout.id)
    } catch {
      clearLayoutProcessing(layout.id)
      ToastNotifications.negative(
        t('Sorry there was a problem deleting this layout, please try again')
      )
    }
  }, [
    clearLayoutProcessing,
    getLayouts,
    quickKeysManager,
    setLayoutProcessing,
    t,
    toDelete,
  ])

  return (
    <>
      <ul className="wr-qk-layouts">
        {layouts.map(layout => (
          <li className="wr-qk-layout" key={layout.id}>
            <div className="wr-qk-layout-about">
              <span className="vd-header wr-qk-layout-title">
                {layout.name}
                {inUseLayouts[layout.id]?.length ? (
                  <Text tag="div" type="supplementary" className="vd-mt1">
                    <T
                      _str="{count, plural, one {Used by {count} other register} other {Used by {count} other registers}}"
                      count={inUseLayouts[layout.id].length}
                    />
                  </Text>
                ) : null}
              </span>
            </div>
            <div className="wr-qk-layout-actions vd-btn-group">
              {isCurrentLayout(layout) ? (
                <Flag className="vd-mr5" content={t('Current Layout')} />
              ) : (
                <Button
                  variant="supplementary"
                  onClick={() => setCurrentLayout(layout)}
                  loading={processing.includes(layout.id)}
                  data-testid="set-current-layout-button"
                >
                  <T _str="Set as Current Layout" />
                </Button>
              )}
              <Button
                variant="supplementary"
                modifier="icon"
                disabled={processing.includes(layout.id)}
                onClick={() =>
                  $state.go(STATES.SETTINGS_QUICK_KEYS_EDIT, {
                    layoutId: layout.id,
                  })
                }
                data-testid="edit-layout-button"
              >
                <Icon icon="pencil" />
              </Button>
              <Button
                variant="supplementary"
                modifier="icon"
                disabled={processing.includes(layout.id)}
                onClick={() => duplicateLayout(layout)}
                data-testid="duplicate-layout-button"
              >
                <Icon icon="files-o" />
              </Button>
              <Button
                variant="supplementary"
                modifier="icon"
                disabled={
                  processing.includes(layout.id) || isCurrentLayout(layout)
                }
                onClick={() => openDeleteModal(layout)}
                data-testid="delete-layout-button"
              >
                <Icon icon="trash" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {toDelete && (
        <DeleteQuickKeyLayoutModal
          deleteLayout={deleteLayout}
          onClose={closeDeleteModal}
        />
      )}
    </>
  )
}
